import { formatLocalDateKey } from "../../utils/dateRangeUtils"
import { socialNetworkTypeIdToChartKey } from "./messagesReport"
import { normalizeDashboardPlatformKey } from "./socialPerformanceReport"
import {
  dashboardPlatformKeyFromDescription,
  socialNetworkDescription,
  socialNetworkTypeIdToDashboardPlatformKey,
} from "./socialNetworkCatalog"

/** ISO date param for GET …/events/performance?startDate=&endDate= */
export function formatCampaignPerformanceDate(date) {
  return formatLocalDateKey(date)
}

export function buildClientEventsPerformanceQuery({ startDate, endDate } = {}) {
  const params = {}
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  return new URLSearchParams(params).toString()
}

export function buildCampaignPerformanceFetchKey(clientId, startDate, endDate) {
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}`
}

export const CAMPAIGN_TOOLTIP_CHANNEL_ORDER = [
  "instagram",
  "facebook",
  "linkedin",
  "twitter",
  "tiktok",
]

export function unwrapCampaignPerformanceEvents(data) {
  if (Array.isArray(data?.events)) return data.events
  if (Array.isArray(data?.data?.events)) return data.data.events
  return []
}

export function normalizeCampaignPerformancePayload(data) {
  return unwrapCampaignPerformanceEvents(data)
}

function resolveCampaignPlatformKey(typeId, socialNetworkTypes = [], network = null) {
  const fromType =
    socialNetworkTypeIdToChartKey(socialNetworkTypes, typeId) ??
    socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
  if (fromType) return fromType
  const name = network?.social_network ?? network?.socialNetwork
  const fromName = dashboardPlatformKeyFromDescription(name)
  if (fromName) return fromName
  const d = socialNetworkDescription(name)
  if (d === "instagram") return "instagram"
  return null
}

function platformKeyMatchesFilter(platformKey, platformFilter) {
  if (!platformFilter) return true

  const normalizedKey = normalizeDashboardPlatformKey(platformKey)
  const selectedKeys =
    platformFilter instanceof Set
      ? [...platformFilter]
      : Array.isArray(platformFilter)
        ? platformFilter
        : []

  if (selectedKeys.length === 0) return true
  return selectedKeys.some(
    (selectedKey) => normalizeDashboardPlatformKey(selectedKey) === normalizedKey,
  )
}

/** True when at least one chart row has impressions or engagements. */
export function campaignChartRowsHaveMetrics(rows) {
  return (Array.isArray(rows) ? rows : []).some(
    (row) => (Number(row?.impressions) || 0) > 0 || (Number(row?.engagements) || 0) > 0,
  )
}

function createEmptyChannelBreakdown() {
  return Object.fromEntries(
    CAMPAIGN_TOOLTIP_CHANNEL_ORDER.map((key) => [key, { impressions: 0, engagements: 0 }]),
  )
}

/**
 * Normalize API events into Campaign Performance chart rows.
 * Sums `by_network` totals per event; keeps per-channel breakdown for tooltips.
 */
export function buildCampaignPerformanceChartData(
  events,
  platformFilter = null,
  socialNetworkTypes = [],
) {
  const list = Array.isArray(events) ? events : []

  return list.map((event) => {
    const eventName = event?.event_name ?? event?.eventName ?? "Unknown"
    const networks = Array.isArray(event?.by_network)
      ? event.by_network
      : Array.isArray(event?.byNetwork)
        ? event.byNetwork
        : []

    const channelBreakdown = createEmptyChannelBreakdown()
    let impressions = 0
    let engagements = 0

    for (const network of networks) {
      const typeId = Number(network?.social_network_type_id ?? network?.socialNetworkTypeId)
      const platformKey = resolveCampaignPlatformKey(typeId, socialNetworkTypes, network)
      if (!platformKey) continue
      if (!platformKeyMatchesFilter(platformKey, platformFilter)) continue

      const netImpressions =
        Number(network?.total_impressions ?? network?.totalImpressions) || 0
      const netEngagements =
        Number(network?.total_engagements ?? network?.totalEngagements) || 0

      channelBreakdown[platformKey].impressions += netImpressions
      channelBreakdown[platformKey].engagements += netEngagements
      impressions += netImpressions
      engagements += netEngagements
    }

    const postCount = countEventPosts(event, platformFilter, socialNetworkTypes)

    return {
      eventId: event?.event_id ?? event?.eventId ?? null,
      eventName,
      impressions,
      engagements,
      postCount,
      channelBreakdown,
    }
  })
}

function eventNetworkEntries(event) {
  if (Array.isArray(event?.by_network)) return event.by_network
  if (Array.isArray(event?.byNetwork)) return event.byNetwork
  if (Array.isArray(event?.posts)) return event.posts
  return []
}

/** One `by_network` (or `posts`) entry per published post for the event. */
export function countEventPosts(
  event,
  platformFilter = null,
  socialNetworkTypes = [],
) {
  const networks = eventNetworkEntries(event)
  if (!platformFilter) return networks.length

  let count = 0
  for (const network of networks) {
    const typeId = Number(network?.social_network_type_id ?? network?.socialNetworkTypeId)
    const platformKey = resolveCampaignPlatformKey(typeId, socialNetworkTypes, network)
    if (!platformKey) continue
    if (platformKeyMatchesFilter(platformKey, platformFilter)) count += 1
  }
  return count
}

export function countCampaignPerformancePosts(
  events,
  platformFilter = null,
  socialNetworkTypes = [],
) {
  const list = Array.isArray(events) ? events : []
  return list.reduce(
    (sum, event) => sum + countEventPosts(event, platformFilter, socialNetworkTypes),
    0,
  )
}

export function sumCampaignChartPostCount(chartRows) {
  return (Array.isArray(chartRows) ? chartRows : []).reduce(
    (sum, row) => sum + (Number(row?.postCount) || 0),
    0,
  )
}

export function buildCampaignEventChannelBreakdown(chartRows) {
  const map = new Map()
  for (const row of chartRows) {
    map.set(row.eventName, row.channelBreakdown ?? createEmptyChannelBreakdown())
  }
  return map
}
